import { Avatar, CircularProgress, ListItem, ListItemAvatar, ListItemText, IconButton, Menu, MenuItem, Box } from "@mui/material";
import React from "react";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useCache, { IMAGE_ORTHOPHOTO, IMAGE_SIZE_THUMBNAIL } from "../hooks/useCache";
import AnalysisLink from "./AnalysisLink";
import { useTranslation } from "react-i18next";

export interface AnalysisRowProps {
  analysis: {
    uuid: string;
    created_at: string;
    state: string;
    agriculture_analysis_data: {
      orthophoto_png: string,
    },
    field: {
      name: string;
      uuid: string;
    }
  }
  prefix: string;
  onRemove?: (analysisUuid: string) => void;
}

function AnalysisRow(props: AnalysisRowProps) {
  const [ imageURL, setImageURL ] = React.useState<string | undefined>();
  const { cacheInitialized, getPresignedUrl, progress } = useCache();

  const { t } = useTranslation();
  
  // Menu state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    setAnchorEl(null);
  };
  
  const handleMenuItemClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };
  
  const handleRemoveClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (props.onRemove) {
      props.onRemove(props.analysis.uuid);
    }
    setAnchorEl(null);
  };

  React.useEffect(() => {
    if (cacheInitialized && props.analysis) {
      getPresignedUrl(props.analysis.uuid, IMAGE_ORTHOPHOTO, IMAGE_SIZE_THUMBNAIL).then((url) => {
        setImageURL(url);
      }).catch((e) => {
        console.error("Failed to download presigned image", e);
      });
    }
  }, [cacheInitialized]);

  return (
    <AnalysisLink analysis={props.analysis} prefix={props.prefix}>
      <ListItem alignItems="flex-start">
        <ListItemAvatar>
          { (props.analysis.state === 'running' || !imageURL) && <div style={{
            borderRadius: 10,
            height: 50,
            width: 50,
            backgroundColor: 'var(--gray-2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <CircularProgress size={30}
              variant={progress > 0 ? 'determinate' : 'indeterminate'}
              value={progress > 0 ? progress * 100 : undefined} />
          </div> }
          { props.analysis.state !== 'running' && imageURL && <Avatar src={imageURL} variant="rounded" style={{
            borderRadius: 10,
            height: 50,
            width: 50,
          }} /> }
        </ListItemAvatar>
        <ListItemText
          primary={new Date(props.analysis.created_at).toLocaleDateString() + (props.analysis.field ? (' - ' + props.analysis.field.name) : '')}
          secondary={
            <React.Fragment>
              {props.analysis.state}
            </React.Fragment>
          }
        />
        <Box onClick={(e) => e.stopPropagation()}>
          <IconButton
            aria-label="more"
            aria-controls="analysis-menu"
            aria-haspopup="true"
            onClick={handleMenuClick}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="analysis-menu"
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuItemClick}
          >
            <MenuItem onClick={handleRemoveClick}>
              <Box display="flex" alignItems="center">
                <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                {t('Remove')}
              </Box>
            </MenuItem>
          </Menu>
        </Box>
      </ListItem>
    </AnalysisLink>
  );
}

export default AnalysisRow;
